/*
 * Spielefürst in-house wishlist (replaces Swish/Wishlist King).
 *
 * Storage model: localStorage only (device-local, guests included) — matches
 * the previous app's UNRESTRICTED guest mode. Cross-device sync would need a
 * backend and is intentionally out of scope for v1.
 *
 * Custom elements:
 *   <wishlist-toggle handle="...">  toggle button (product page + cards)
 *   <wishlist-count>                header count bubble
 *   <wishlist-grid>                 wishlist page renderer
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'sf:wishlist:v1';
  const PAGE_URL = '/pages/wunschliste';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  };

  const notify = () => {
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: read(), count: read().length } }));
  };

  const write = (handles) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      /* storage unavailable (private mode etc.) — wishlist is then session-only in memory: nothing to do */
    }
    notify();
  };

  const wishlist = {
    get handles() { return read(); },
    get count() { return read().length; },
    pageUrl: PAGE_URL,
    has(handle) { return read().includes(handle); },
    add(handle) {
      const list = read();
      if (!list.includes(handle)) {
        list.unshift(handle);
        write(list);
      }
    },
    remove(handle) { write(read().filter((h) => h !== handle)); },
    toggle(handle) {
      this.has(handle) ? this.remove(handle) : this.add(handle);
      return this.has(handle);
    }
  };
  window.SFWishlist = wishlist;

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) notify();
  });

  const formatMoney = (cents) => {
    let currency = 'EUR';
    try { currency = window.Shopify?.currency?.active || 'EUR'; } catch { /* keep EUR */ }
    const locale = document.documentElement.lang || 'de';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2) + ' €';
    }
  };

  const imageUrl = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&#39;');

  /* ------------------------------------------------------------------ */

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this._button = this.querySelector('button');
      if (!this._button) return;
      this._abortController = new AbortController();
      this._button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        wishlist.toggle(this.getAttribute('handle'));
      }, { signal: this._abortController.signal });
      document.addEventListener('wishlist:change', () => this._sync(), { signal: this._abortController.signal });
      this._sync();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _sync() {
      const active = wishlist.has(this.getAttribute('handle'));
      this._button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active ? this._button.dataset.labelRemove : this._button.dataset.labelAdd;
      if (label) {
        this._button.setAttribute('aria-label', label);
        const text = this._button.querySelector('.wishlist-toggle__label');
        if (text) text.textContent = label;
      }
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      document.addEventListener('wishlist:change', () => this._sync(), { signal: this._abortController.signal });
      this._sync();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _sync() {
      const count = wishlist.count;
      const number = this.querySelector('[data-wishlist-count-number]');
      if (number) number.textContent = String(count);
      this.classList.toggle('opacity-0', count === 0);
    }
  }

  /* ------------------------------------------------------------------ */

  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this._items = this.querySelector('[data-wishlist-items]');
      this._empty = this.querySelector('[data-wishlist-empty]');
      this._loading = this.querySelector('[data-wishlist-loading]');
      this._abortController = new AbortController();
      document.addEventListener('wishlist:change', () => this._render(), { signal: this._abortController.signal });
      this._render();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    async _render() {
      const handles = wishlist.handles;
      if (this._loading) this._loading.hidden = this._rendered === true;

      if (handles.length === 0) {
        if (this._items) this._items.innerHTML = '';
        if (this._empty) this._empty.hidden = false;
        if (this._loading) this._loading.hidden = true;
        this._rendered = true;
        return;
      }
      if (this._empty) this._empty.hidden = true;

      const results = await Promise.all(handles.map(async (handle) => {
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
          if (response.status === 404) {
            wishlist.remove(handle); /* product no longer exists */
            return null;
          }
          if (!response.ok) return { handle, error: true };
          return await response.json();
        } catch {
          return { handle, error: true };
        }
      }));

      /* a remove() above re-triggers _render via wishlist:change; bail out to avoid double paint */
      if (results.includes(null)) return;

      const fragment = document.createDocumentFragment();
      for (const product of results) {
        if (!product || product.error) continue;
        fragment.appendChild(this._buildCard(product));
      }
      if (this._items) this._items.replaceChildren(fragment);
      if (this._loading) this._loading.hidden = true;
      this._rendered = true;
    }

    _buildCard(product) {
      const singleVariant = product.variants.length === 1;
      const image = imageUrl(product.featured_image, 600);
      const price = formatMoney(product.price);

      const card = document.createElement('article');
      card.className = 'wishlist-card';
      card.innerHTML = `
        <div class="wishlist-card__figure">
          <a href="${escapeHtml(product.url)}">
            ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          </a>
          <button type="button" class="wishlist-card__remove" aria-label="Von der Wunschliste entfernen" title="Von der Wunschliste entfernen">
            <svg role="presentation" focusable="false" width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
          </button>
        </div>
        <div class="wishlist-card__info">
          <a class="wishlist-card__title bold" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <span class="wishlist-card__price">${product.available ? escapeHtml(price) : 'Ausverkauft'}</span>
          <div class="wishlist-card__actions">
            ${product.available
              ? (singleVariant
                  ? `<button type="button" class="button button--sm" data-variant-id="${product.variants[0].id}">In den Warenkorb</button>`
                  : `<a class="button button--sm" href="${escapeHtml(product.url)}">Optionen wählen</a>`)
              : `<a class="button button--sm button--outline" href="${escapeHtml(product.url)}">Produkt ansehen</a>`}
          </div>
        </div>`;

      card.querySelector('.wishlist-card__remove').addEventListener('click', () => {
        wishlist.remove(product.handle);
      });

      const atcButton = card.querySelector('[data-variant-id]');
      if (atcButton) {
        atcButton.addEventListener('click', () => this._addToCart(atcButton));
      }
      return card;
    }

    async _addToCart(button) {
      const variantId = Number(button.dataset.variantId);
      button.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!response.ok) throw new Error('add failed');
        /* theme (Impact) listens for these on document: refresh cart content, then open the drawer */
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: {} }));
      } catch {
        window.location.href = '/cart';
        return;
      } finally {
        button.disabled = false;
      }
    }
  }

  if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
})();
